import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, DropdownProps, Icon, Input } from 'semantic-ui-react';
import { listResidents } from '../../services/Resident';
import { Registrant } from '../../types/Registrant';
import sendToast from '../../../utils/Toast';

interface Props {
    facilityId: string;
    residentId?: string;
    groupResidentIds?: string[];
    onProfileClick?: (resident: Registrant) => void;
}

const PAGE_SIZE = 40;
const SEARCH_DEBOUNCE_DELAY = 700;
type SortOptionValue = 'fn-az' | 'fn-za' | 'ln-az' | 'ln-za';
type SortOption = {
    key: SortOptionValue;
    text: string;
    value: SortOptionValue;
    apiSort: Record<string, string>[];
};

// Backend sort is inverted: "desc" → MongoDB 1 (asc), "asc" → MongoDB -1 (desc)
const SORT_OPTIONS: SortOption[] = [
    { key: 'fn-az', text: 'First Name A-Z', value: 'fn-az', apiSort: [{ FirstName: 'desc' }] },
    { key: 'fn-za', text: 'First Name Z-A', value: 'fn-za', apiSort: [{ FirstName: 'asc' }] },
    { key: 'ln-az', text: 'Last Name A-Z', value: 'ln-az', apiSort: [{ LastName: 'desc' }] },
    { key: 'ln-za', text: 'Last Name Z-A', value: 'ln-za', apiSort: [{ LastName: 'asc' }] },
];
const SORT_DROPDOWN_OPTIONS = SORT_OPTIONS.map(({ key, text, value }) => ({ key, text, value }));

const DEFAULT_SORT = SORT_OPTIONS[2]; // Last Name A-Z

const DirectorySection = ({ facilityId, residentId, groupResidentIds, onProfileClick }: Props) => {
    const [residents, setResidents] = useState<Registrant[]>([]);
    const [totRecords, setTotRecords] = useState(0);
    const [pageNo, setPageNo] = useState(1);
    const [searchKey, setSearchKey] = useState('');
    // Trimmed text last sent. The debounce compares against this so a trailing space
    // does not fire a second request, and a group change does not schedule one either.
    const [requestedQuery, setRequestedQuery] = useState('');
    // Trimmed text the visible rows belong to. A page-1 response that is still
    // the latest sets this on success. A failed page-1 clears the rows and sets
    // this too, so the box matches an explicit error instead of the previous list.
    const [appliedQuery, setAppliedQuery] = useState('');
    const [listFailed, setListFailed] = useState(false);
    const [currentSort, setCurrentSort] = useState(DEFAULT_SORT);
    const [isLoading, setIsLoading] = useState(false);
    const searchTimeoutRef = useRef<number>();
    const requestIdRef = useRef(0);
    const sortRef = useRef(currentSort)
    sortRef.current = currentSort;

    const resultsMatchInput = searchKey.trim() === appliedQuery;

    const fetchResidentsList = async (page: number, search?: string, reset = false, sort?: Record<string, string>[], residentIds?: string[]) => {
        const requestId = ++requestIdRef.current;
        const query = (search ?? '').trim();
        try {
            setIsLoading(true);
            const response = await listResidents({
                facilityId,
                search: query,
                page_no: page,
                page_size: PAGE_SIZE,
                sort: sort || sortRef.current.apiSort,
                residentIds,
                residentId,
            });
            if (requestId !== requestIdRef.current) return;
            if (reset) {
                setResidents(response.Residents);
                setAppliedQuery(query);
            } else {
                setResidents(prev => [...prev, ...response.Residents]);
            }
            setPageNo(page);
            setTotRecords(response.TotRecords);
            setListFailed(false);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            sendToast('error', error instanceof Error ? error.message : 'Error fetching residents');
            if (reset) {
                setResidents([]);
                setTotRecords(0);
                setAppliedQuery(query);
                setListFailed(true);
            }
        } finally {
            if (requestId === requestIdRef.current) setIsLoading(false);
        }
    };

    const handleRetry = () => {
        fetchResidentsList(1, appliedQuery, true, undefined, groupResidentIds);
    };

    useEffect(() => {
        window.clearTimeout(searchTimeoutRef.current);
        setSearchKey('');
        setRequestedQuery('');
        setAppliedQuery('');
        setResidents([]);
        setTotRecords(0);
        setListFailed(false);
        fetchResidentsList(1, undefined, true, undefined, groupResidentIds);
    }, [facilityId, residentId, groupResidentIds]);

    const handleSearch = (raw: string) => {
        window.clearTimeout(searchTimeoutRef.current);
        const query = raw.trim();
        setRequestedQuery(query);
        fetchResidentsList(1, query, true, undefined, groupResidentIds);
    };

    useEffect(() => {
        const query = searchKey.trim();
        if (query === requestedQuery) return undefined;
        searchTimeoutRef.current = window.setTimeout(() => {
            handleSearch(searchKey);
        }, SEARCH_DEBOUNCE_DELAY);
        return () => window.clearTimeout(searchTimeoutRef.current);
    }, [searchKey, requestedQuery]);

    const handleSortChange = (_e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
        const value = typeof data.value === 'string' ? data.value : DEFAULT_SORT.value;
        const selected = SORT_OPTIONS.find(o => o.value === value) || DEFAULT_SORT;
        setCurrentSort(selected);
        setRequestedQuery(appliedQuery);
        fetchResidentsList(1, appliedQuery, true, selected.apiSort, groupResidentIds);
    };

    const handleLoadMore = () => {
        if (isLoading || !resultsMatchInput) return;
        fetchResidentsList(pageNo + 1, appliedQuery, false, undefined, groupResidentIds);
    };

    const showLoadMore = residents.length < totRecords;

    return (
        <div className="directory-section">
            <div className="section-header">
                <Icon name="address book" />
                <span>Directory</span>
            </div>
            <div className="directory-search">
                <Input
                    size="small"
                    icon="search"
                    iconPosition="left"
                    placeholder="Search residents…"
                    value={searchKey}
                    loading={isLoading}
                    onChange={(_e, data) => setSearchKey(data.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSearch(searchKey);
                    }}
                />
                <Dropdown
                    icon="sort"
                    floating
                    button
                    className="icon directory-sort"
                    options={SORT_DROPDOWN_OPTIONS}
                    value={currentSort.value}
                    onChange={handleSortChange}
                    trigger={<></>}
                />
            </div>
            <div className="directory-list">
                {residents.map(resident => {
                    const fullName = ((resident.FirstName || '') + ' ' + (resident.LastName || '')).trim();
                    return (
                        <div
                            key={resident._id}
                            className="directory-entry"
                            role="button"
                            tabIndex={0}
                            onClick={() => onProfileClick?.(resident)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onProfileClick?.(resident);
                                }
                            }}
                        >
                            <img
                                src={resident.Image || '/avatar_placeholder.png'}
                                alt={fullName}
                                className="directory-avatar"
                            />
                            <div className="directory-info">
                                <span className="directory-name">{fullName}</span>
                                <span className="directory-room">
                                    {resident.UnitName ? `Room ${resident.UnitName}` : ''}
                                </span>
                            </div>
                            <Icon name="comment outline" circular size="small" className="directory-profile-icon" />
                        </div>
                    );
                })}
                {listFailed && resultsMatchInput && (
                    <div className="directory-empty">
                        <p>Could not load residents.</p>
                        <Button type="button" size="small" onClick={handleRetry} loading={isLoading} disabled={isLoading}>
                            Retry
                        </Button>
                    </div>
                )}
                {!isLoading && !listFailed && resultsMatchInput && residents.length === 0 && (
                    <p className="directory-empty">No residents found</p>
                )}
            </div>
            {showLoadMore && resultsMatchInput && !listFailed && (
                <div className="load-more-button">
                    <Button onClick={handleLoadMore} loading={isLoading} disabled={isLoading} size="small">
                        Load More
                    </Button>
                </div>
            )}
        </div>
    );
};

export default DirectorySection;
